// Calendar Provider Types
export enum CalendarDomain {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
  APPLE = 'apple',
}

export type CalendarType = 'work' | 'personal' | 'other';

// Google Calendar Event Interface
export interface IGoogleCalendarEvent {
  kind?: string; // Type of the resource
  etag?: string; // ETag of the resource
  id?: string; // Identifier of the event
  status?: string; // Status of the event (e.g., confirmed, cancelled)
  htmlLink?: string; // Link to the event in the Google Calendar web interface
  created?: string; // Creation time of the event
  updated?: string; // Last modification time of the event
  summary?: string; // Title of the event
  description?: string; // Description of the event
  location?: string; // Location of the event
  creator?: {
    email?: string; // Email of the creator
    displayName?: string; // Display name of the creator
    id?: string; // ID of the creator
  };
  organizer?: {
    email?: string; // Email of the organizer
    displayName?: string; // Display name of the organizer
    id?: string; // ID of the organizer
  };
  start?: {
    date?: string; // Start date (YYYY-MM-DD)
    dateTime?: string; // Start date and time (RFC3339)
    timeZone?: string; // Time zone of the start time
  };
  end?: {
    date?: string; // End date (YYYY-MM-DD)
    dateTime?: string; // End date and time (RFC3339)
    timeZone?: string; // Time zone of the end time
  };
  attendees?: Array<{
    email?: string; // Email of the attendee
    displayName?: string; // Display name of the attendee
    responseStatus?: string; // Response status of the attendee
  }>;
  reminders?: {
    useDefault?: boolean; // Whether to use default reminders
    overrides?: Array<{
      method?: string; // Method of reminder (e.g., email, popup)
      minutes?: number; // Minutes before the event to send the reminder
    }>;
  };
}

// Domain-specific data interfaces
export interface IGoogleCalendarData {
  accessToken?: string;
  refreshToken?: string;
  calendarId?: string;
  providerId?: string;
  googleAuthResponse?: {
    access_token: string;
    refresh_token: string;
    scope: string;
    token_type: string;
    id_token: string;
    expires_in?: number;
    session_state?: {
      extraQueryParams?: {
        authuser?: string;
      };
    };
    first_issued_at?: number;
    expires_at?: number;
    idpId?: string;
    login_hint?: string;
  };
}

export interface IOutlookCalendarData {
  accessToken?: string;
  calendarId?: string;
  refreshToken?: string;
  expiresAt?: number;
}

export interface IAppleCalendarData {
  // Any Apple-specific data that isn't common across providers
}

// Main calendar interface
export interface IUserCalendar {
  id: string;
  email: string;
  domain: CalendarDomain;
  isPrimary?: boolean;
  type?: CalendarType;
  tags?: string[];
  timezone?: string;
  isActive?: boolean; // Replaces both 'connected' and 'tokenStatus' - simple boolean
  domainData?: {
    google?: IGoogleCalendarData;
    outlook?: IOutlookCalendarData;
    apple?: IAppleCalendarData;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ITaskCalendarEvent {
  userCalendarId: string;
  domain: CalendarDomain;
  type?: CalendarType;
  tags?: string[];
  domainData: {
    google?: IGoogleEvent;
    outlook?: IOutlookEvent;
    // apple?: IAppleCalendarEvent;
  };
}

///////////////////////////////////////////////////////////////////
// Outlook Calendar Event Interface //////////////////////////////
///////////////////////////////////////////////////////////////////

export interface IOutlookEvent {
  '@odata.etag'?: string;
  id?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  changeKey?: string;
  categories?: string[];
  transactionId?: string;
  originalStartTimeZone?: string;
  originalEndTimeZone?: string;
  iCalUId?: string;
  uid?: string;
  reminderMinutesBeforeStart?: number;
  isReminderOn?: boolean;
  hasAttachments?: boolean;
  subject?: string;
  bodyPreview?: string;
  importance?: 'low' | 'normal' | 'high';
  sensitivity?: 'normal' | 'personal' | 'private' | 'confidential';
  isAllDay?: boolean;
  isCancelled?: boolean;
  isOrganizer?: boolean;
  responseRequested?: boolean;
  seriesMasterId?: string | null;
  showAs?:
    | 'free'
    | 'tentative'
    | 'busy'
    | 'oof'
    | 'workingElsewhere'
    | 'unknown';
  type?: 'singleInstance' | 'occurrence' | 'exception' | 'seriesMaster';
  webLink?: string;
  onlineMeetingUrl?: string | null;
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?:
    | 'unknown'
    | 'teamsForBusiness'
    | 'skypeForBusiness'
    | 'skypeForConsumer';
  allowNewTimeProposals?: boolean;
  occurrenceId?: string | null;
  isDraft?: boolean;
  hideAttendees?: boolean;

  responseStatus?: {
    response?:
      | 'none'
      | 'organizer'
      | 'tentativelyAccepted'
      | 'accepted'
      | 'declined'
      | 'notResponded';
    time?: string;
  };

  body?: {
    contentType?: 'html' | 'text';
    content?: string;
  };

  start?: {
    dateTime?: string;
    timeZone?: string;
  };

  end?: {
    dateTime?: string;
    timeZone?: string;
  };

  location?: {
    displayName?: string;
    locationType?:
      | 'default'
      | 'conferenceRoom'
      | 'homeAddress'
      | 'businessAddress'
      | 'geoCoordinates'
      | 'streetAddress'
      | 'unknown';
    uniqueId?: string;
    uniqueIdType?: 'locationStore' | 'directory' | 'private' | 'unknown';
    address?: Record<string, any>;
    coordinates?: Record<string, any>;
  };

  locations?: Array<{
    displayName?: string;
    locationType?: string;
    uniqueId?: string;
    uniqueIdType?: string;
    address?: Record<string, any>;
    coordinates?: Record<string, any>;
  }>;

  recurrence?: {
    pattern?: {
      type?:
        | 'daily'
        | 'weekly'
        | 'absoluteMonthly'
        | 'relativeMonthly'
        | 'absoluteYearly'
        | 'relativeYearly';
      interval?: number;
      month?: number;
      dayOfMonth?: number;
      daysOfWeek?: string[];
      firstDayOfWeek?: string;
      index?: 'first' | 'second' | 'third' | 'fourth' | 'last';
    };
    range?: {
      type?: 'endDate' | 'noEnd' | 'numbered';
      startDate?: string;
      endDate?: string;
      numberOfOccurrences?: number;
      recurrenceTimeZone?: string;
    };
  };

  attendees?: Array<{
    emailAddress?: {
      name?: string;
      address?: string;
    };
    type?: 'required' | 'optional' | 'resource';
    status?: {
      response?:
        | 'none'
        | 'organizer'
        | 'tentativelyAccepted'
        | 'accepted'
        | 'declined'
        | 'notResponded';
      time?: string;
    };
  }>;

  organizer?: {
    emailAddress?: {
      name?: string;
      address?: string;
    };
  };

  onlineMeeting?: {
    joinUrl?: string;
    conferenceId?: string;
    tollNumber?: string;
    tollFreeNumber?: string;
    quickDial?: string;
    joinInformation?: {
      contentType?: string;
      content?: string;
    };
  };
}

///////////////////////////////////////////////////////////////////
// Google Calendar Event Interface ////////////////////////////////
///////////////////////////////////////////////////////////////////
// Full replica of Google Calendar API v3 Event interface and all dependent types

export interface IGoogleEvent {
  /** Whether anyone can invite themselves to the event (deprecated). */
  anyoneCanAddSelf?: boolean | null;

  /** File attachments for the event. Max 25. */
  attachments?: IEventAttachment[];

  /** Attendees of the event. */
  attendees?: IEventAttendee[];

  /** Indicates if attendees were omitted due to maxAttendee parameter. */
  attendeesOmitted?: boolean | null;

  /** Color ID referring to color definitions. */
  colorId?: string | null;

  /** Google Meet or conference-related info. */
  conferenceData?: IConferenceData;

  /** Creation timestamp (RFC3339). Read-only. */
  created?: string | null;

  /** Event creator info. */
  creator?: IEventPerson | null;

  /** Description of the event (can contain HTML). */
  description?: string | null;

  /** End datetime. */
  end?: IEventDateTime;

  /** Whether end time is unspecified. */
  endTimeUnspecified?: boolean | null;

  /** ETag for resource versioning. */
  etag?: string | null;

  /** Type of event (e.g., default, outOfOffice). */
  eventType?: string | null;

  /** Extended event properties. */
  extendedProperties?: {
    private?: { [key: string]: string };
    shared?: { [key: string]: string };
  } | null;

  /** Focus time metadata. */
  focusTimeProperties?: IEventFocusTimeProperties;

  /** Deprecated gadget object, used for birthday calendar metadata. */
  gadget?: {
    display?: string;
    height?: number;
    iconLink?: string;
    link?: string;
    preferences?: { [key: string]: string };
    title?: string;
    type?: string;
    width?: number;
  } | null;

  guestsCanInviteOthers?: boolean | null;
  guestsCanModify?: boolean | null;
  guestsCanSeeOtherGuests?: boolean | null;
  hangoutLink?: string | null;
  htmlLink?: string | null;
  iCalUID?: string | null;
  id?: string | null;
  kind?: string | null;
  location?: string | null;
  locked?: boolean | null;
  organizer?: IEventPerson | null;
  originalStartTime?: IEventDateTime;
  outOfOfficeProperties?: IEventOutOfOfficeProperties;
  privateCopy?: boolean | null;
  recurrence?: string[] | null;
  recurringEventId?: string | null;
  reminders?: {
    overrides?: IEventReminder[];
    useDefault?: boolean;
  } | null;
  sequence?: number | null;
  source?: {
    title?: string;
    url?: string;
  } | null;
  start?: IEventDateTime;
  status?: string | null;
  summary?: string | null;
  transparency?: string | null;
  updated?: string | null;
  visibility?: string | null;
  workingLocationProperties?: IEventWorkingLocationProperties;
}

// Supporting Interfaces

export interface IEventAttachment {
  fileUrl?: string;
  iconLink?: string;
  mimeType?: string;
  title?: string;
}

export interface IEventAttendee {
  displayName?: string;
  email?: string;
  organizer?: boolean;
  resource?: boolean;
  responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  self?: boolean;
  optional?: boolean;
  comment?: string;
  additionalGuests?: number;
}

export interface IConferenceData {
  conferenceId?: string;
  entryPoints?: Array<{
    entryPointType?: string;
    uri?: string;
    label?: string;
    pin?: string;
    accessCode?: string;
    meetingCode?: string;
    passcode?: string;
    password?: string;
  }>;
  conferenceSolution?: {
    name?: string;
    iconUri?: string;
    key?: {
      type?: string;
    };
  };
  notes?: string;
}

export interface IEventReminder {
  method?: 'email' | 'popup';
  minutes?: number;
}

export interface IEventDateTime {
  date?: string;
  dateTime?: string;
  timeZone?: string;
}

export interface IEventFocusTimeProperties {
  focusTimeType?: string;
}

export interface IEventOutOfOfficeProperties {
  autoDeclineMode?: string;
  declineMessage?: string;
  declineReason?: string;
}

export interface IEventWorkingLocationProperties {
  type?: string;
  homeOfficeLocation?: { label?: string };
  customLocation?: { label?: string };
  officeLocation?: {
    buildingId?: string;
    floorId?: string;
    floorSectionId?: string;
    label?: string;
  };
}

export interface IEventPerson {
  displayName?: string;
  email?: string;
  id?: string;
  self?: boolean;
}

/** 
interface GoogleCalendarEvent {
  id?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  htmlLink?: string;
  created?: string; // ISO 8601 format
  updated?: string; // ISO 8601 format
  summary?: string;
  description?: string;
  location?: string;
  colorId?: string;
  creator?: {
    id?: string;
    email?: string;
    displayName?: string;
    self?: boolean;
  };
  organizer?: {
    id?: string;
    email?: string;
    displayName?: string;
    self?: boolean;
  };
  start: {
    date?: string; // All-day event (YYYY-MM-DD)
    dateTime?: string; // Time-specific event (ISO 8601)
    timeZone?: string;
  };
  end: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
  };
  endTimeUnspecified?: boolean;
  recurrence?: string[]; // e.g., ["RRULE:FREQ=WEEKLY;BYDAY=MO"]
  recurringEventId?: string;
  originalStartTime?: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
  };
  transparency?: 'opaque' | 'transparent';
  visibility?: 'default' | 'public' | 'private' | 'confidential';
  iCalUID?: string;
  sequence?: number;
  attendees?: Array<{
    id?: string;
    email: string;
    displayName?: string;
    organizer?: boolean;
    self?: boolean;
    resource?: boolean;
    optional?: boolean;
    responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
    comment?: string;
    additionalGuests?: number;
  }>;
  attendeesOmitted?: boolean;
  hangoutLink?: string;
  conferenceData?: any; // Can be deeply structured (Google Meet, etc.)
  reminders?: {
    useDefault: boolean;
    overrides?: Array<{
      method: 'email' | 'popup';
      minutes: number;
    }>;
  };
  eventType?: 'default' | 'focusTime' | 'outOfOffice' | 'workingLocation';
  source?: {
    url: string;
    title: string;
  };
  attachments?: Array<{
    fileUrl: string;
    title: string;
    mimeType?: string;
    iconLink?: string;
    fileId?: string;
  }>;
  guestsCanInviteOthers?: boolean;
  guestsCanModify?: boolean;
  guestsCanSeeOtherGuests?: boolean;
  anyoneCanAddSelf?: boolean;
  privateCopy?: boolean;
  locked?: boolean;
}
*/
